package ai.jessie.web.settings

import ai.jessie.shared.settings.OrganisationSettingsResponse
import ai.jessie.shared.settings.TaxonomyEntryResponse
import ai.jessie.web.onboarding.onboardingProblem
import ai.jessie.web.theme.AlertListStyle
import ai.jessie.web.theme.AlertStyle
import ai.jessie.web.theme.ButtonVariant
import ai.jessie.web.theme.CardStackStyle
import ai.jessie.web.theme.DefinitionsStyle
import ai.jessie.web.theme.HintStyle
import ai.jessie.web.theme.PreLineStyle
import ai.jessie.web.theme.RowStyle
import ai.jessie.web.theme.StatusStyle
import ai.jessie.web.theme.SubListStyle
import ai.jessie.web.theme.card
import ai.jessie.web.theme.number
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.silk.style.toAttrs
import kotlinx.browser.document
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.B
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.TagElement
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import org.w3c.dom.HTMLElement

// UC-025 (`docs/design/screens/19-EinstellungenOrganisation.png`): the current settings as
// read-only cards, the single form in their place, the review as a card. The page title, the edit
// action and the side navigation live in the page.
@Composable
fun organisationSettings(organisationId: String, state: SettingsUiState, actions: SettingsActions) {
    val focusReturn = remember { FocusReturn() }
    settingsFocus(state, focusReturn)
    state.error?.let { Div(attrs = AlertStyle.toAttrs()) { onboardingProblem(it, state.busy) } }
    if (state.error?.authenticationRequired != true) {
        if (state.busy) status("Wird geladen oder gespeichert …")
        state.notice?.let { status(it) }
        settingsProblems(state.problems)
        settingsConflictList(state.conflicts)
        val current = state.current
        when {
            state.preview != null -> settingsReview(state, actions, focusReturn)
            state.draft != null ->
                Div(attrs = CardStackStyle.toAttrs()) {
                    current?.let { inboundAddress(it) }
                    settingsForm(state, actions)
                }
            else -> {
                current?.let { settingsView(it) }
                Div(attrs = RowStyle.toAttrs()) {
                    themedButton(
                        "Einstellungen erneut laden",
                        actions.refresh,
                        variant = ButtonVariant.GHOST,
                        disabled = state.busy,
                    )
                }
            }
        }
    }
    P { A(href = "/organisations/$organisationId") { Text("Zurück zur Organisation") } }
}

// Returns keyboard focus to the page title after the review closes, not on first render.
internal class FocusReturn {
    var pending = false
}

// The review sets [FocusReturn.pending]; when it closes (save, return, cancel) the title takes
// focus again. While a problem is shown, the alert or the marked field keeps focus.
@Composable
private fun settingsFocus(state: SettingsUiState, focusReturn: FocusReturn) {
    LaunchedEffect(state.preview == null, state.draft == null) {
        if (state.preview != null) return@LaunchedEffect
        if (focusReturn.pending && state.error == null && !AppGlobals.isExporting)
            (document.getElementById("page-title") as? HTMLElement)?.focus()
        focusReturn.pending = false
    }
}

@Composable
private fun status(text: String) {
    P(attrs = StatusStyle.toAttrs { attr("role", "status") }) { Text(text) }
}

// A2–A5: every invalid field with its correction; marked fields refer to this list.
@Composable
private fun settingsProblems(problems: Map<String, String>) {
    if (problems.isEmpty()) return
    Ul(attrs = AlertListStyle.toAttrs { id("settings-problems") }) {
        problems.forEach { (field, correction) -> Li { Text("${fieldLabel(field)}: $correction") } }
    }
}

// A6: loaded, newly stored and intended values stay visible while the owner re-enters changes.
@Composable
private fun settingsConflictList(conflicts: List<SettingsConflict>) {
    if (conflicts.isEmpty()) return
    Section(attrs = AlertStyle.toAttrs { attr("aria-labelledby", "settings-conflicts-title") }) {
        H2(attrs = { id("settings-conflicts-title") }) { Text("Vergleich mit dem neueren Stand") }
        Ul(attrs = SubListStyle.toAttrs()) {
            conflicts.forEach { row ->
                Li {
                    if (row.conflict) B { Text("Konflikt – ") }
                    Text(
                        "${fieldLabel(row.field)}: geladen ${shown(row.previous)} · " +
                            "jetzt gespeichert ${shown(row.current)} · " +
                            "dein Wert ${shown(row.intended)}"
                    )
                }
            }
        }
    }
}

// Step 2 and A1. Only what the server returned is shown; absent values are never filled in.
@Composable
private fun settingsView(current: OrganisationSettingsResponse) {
    if (!current.editable)
        P(attrs = StatusStyle.toAttrs()) { Text("Finanzeinstellungen (nur lesen)") }
    if (!current.configured)
        P(attrs = HintStyle.toAttrs()) {
            Text("Die Geschäftseinstellungen sind noch nicht eingerichtet. Es gelten keine Werte.")
        }
    val values = current.draft().fields
    Div(attrs = CardStackStyle.toAttrs()) {
        settingsSections.forEach { section ->
            // Hidden from the accountant view (A1); absent for it even when configured.
            val fields =
                section.fields.filter { it != "automaticDocumentExtraction" || current.editable }
            card(section.title, section.description) {
                TagElement<HTMLElement>("dl", DefinitionsStyle.toAttrs()) {
                    fields.forEach { name -> settingDefinition(name, values[name]) }
                }
            }
        }
        vatRateTable(current)
        taxonomyCard("Dokumentkategorien", current.documentCategories)
        taxonomyCard("Dokumentordner", current.documentFolders)
        inboundAddress(current)
    }
}

@Composable
private fun settingDefinition(name: String, value: String?) {
    TagElement<HTMLElement>("dt", null) { Text(viewLabel(name)) }
    TagElement<HTMLElement>("dd", if (name == "address") PreLineStyle.toAttrs() else null) {
        val text = shown(value)
        if (name in numericSettings && text != "–") number(text) else Text(text)
    }
}

@Composable
private fun taxonomyCard(title: String, entries: List<TaxonomyEntryResponse>) {
    card(title) {
        if (entries.isEmpty()) P(attrs = HintStyle.toAttrs()) { Text("Keine Einträge.") }
        else
            Ul(attrs = SubListStyle.toAttrs()) {
                entries.forEach {
                    Li { Text(if (it.active) it.name else "${it.name} (archiviert)") }
                }
            }
    }
}

// BR-175, B3: system-assigned and read-only; never an input.
@Composable
private fun inboundAddress(current: OrganisationSettingsResponse) {
    card("Eingangsadresse für E-Mails", "Vom System vergeben, nicht änderbar.") {
        val address = current.inboundEmail
        if (address == null) Div(attrs = HintStyle.toAttrs()) { Text("noch nicht zugewiesen") }
        else Div { Text(address) }
    }
}

internal fun shown(value: String?) =
    when (value) {
        null,
        "" -> "–"
        "true" -> "ja"
        "false" -> "nein"
        else -> value
    }
